import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import { DEFAULT_NOTIFY, DEFAULT_PRAYER_SETTINGS, dayKeyIn } from '@shared/prayer';
import { DEFAULT_TRACK_SUNNAH, type SunnahKey } from '@/features/prayer/sunnah';
import { rollover, scopeKeys, type AyahDay, type Khatmah } from '@/features/khatmah/khatmah';
import { afterLesson, grade as gradePage, memorisedIds, pageOfId, repositionFor, seedKnown, unlearn, type Grade, type HifzDay, type HifzLessonRec, type HifzPage, type HifzPlan, type Lesson } from '@/features/hifz/hifz';
import type { AdhkarDay, Bookmark, LectureMark, FastDay, FastKind, FastReason, FastStatus, Goal, LastRead, PeriodEntry, PrayerDay, PrayerStatus, Session, Settings, DhikrDay } from './types';

/* IndexedDB, not localStorage: a year of sessions outgrows the 5MB
   localStorage quota, and iOS evicts localStorage more eagerly. Wrapped so a
   private window (where IDB can throw) degrades to in-memory instead of
   crashing the app.

   Writes are serialised per key and coalesced: persist calls setItem on every
   state change, and concurrent IDB transactions can commit out of order — an
   older snapshot landing last silently discards newer data (seen in WebKit
   with rapid updates, e.g. fast taps on the dhikr counter). Only one write per
   key is ever in flight, and when it finishes the latest pending value is
   written next, so the last write always wins. */
const pending = new Map<string, string>();
const inFlight = new Set<string>();

async function drain(k: string) {
  if (inFlight.has(k)) return;
  inFlight.add(k);
  try {
    while (pending.has(k)) {
      const v = pending.get(k)!;
      pending.delete(k);
      try { await idbSet(k, v); } catch { /* in-memory only */ }
    }
  } finally { inFlight.delete(k); }
}

export const idbStorage: StateStorage = {
  getItem: async (k) => { try { return (await idbGet(k)) ?? null; } catch { return null; } },
  setItem: (k, v) => { pending.set(k, v); return drain(k); },
  removeItem: async (k) => { pending.delete(k); try { await idbDel(k); } catch {} },
};

/** Resolves once every queued write has reached IndexedDB. */
export async function flushStorage() {
  while (pending.size || inFlight.size) await new Promise((r) => setTimeout(r, 10));
}

export const uid = () => (crypto.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`);

const deviceTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London'; } catch { return 'Europe/London'; } };

export const DEFAULT_SETTINGS: Settings = {
  theme: 'system',
  accent: 'emerald',
  glass: 55,
  mushaf: 'madani-v2',
  readingMode: 'page',
  arabicSize: 1,
  adhkarScript: 'amiri',
  adhkarTajweed: false,
  hifzTajweed: true,
  hifzListens: 3,
  hifzChainListens: 3,
  adhanWhenLocked: false,
  customDhikr: [],
  dhikrGoals: {},
  translationId: 20, // Saheeh International
  showTranslation: true,
  showTransliteration: false,
  wordByWord: false,
  reciterId: 'alafasy',
  followAudio: true,
  repeat: { ayahRepeat: 1, rangeRepeat: 1, gapMode: 'off', gapSeconds: 3, rate: 1 },
  reciteRepeat: { ayahRepeat: 1, rangeRepeat: 1, gapMode: 'x1', gapSeconds: 3 },
  prayer: { ...DEFAULT_PRAYER_SETTINGS, tz: deviceTz() },
  notify: DEFAULT_NOTIFY,
  hour12: false,
  hijriOffset: 0,
  haptics: true,
  showReciterPhotos: true,
  favoriteReciters: [],
  background: 'scene',
  trackSunnah: DEFAULT_TRACK_SUNNAH,
  gender: null,
  displayName: '',
  syncPeriods: false,
  adhanSound: 'makkah',
  dailyGoalMinutes: 15,
};

/* ── Settings ─────────────────────────────────────────────────────── */
interface SettingsState {
  settings: Settings;
  settingsUpdatedAt: number;
  update: (patch: Partial<Settings>) => void;
  updateDeep: <K extends keyof Settings>(key: K, patch: Partial<Settings[K]>) => void;
  reset: () => void;
}

export const useSettings = create<SettingsState>()(persist(
  (set) => ({
    settings: DEFAULT_SETTINGS,
    settingsUpdatedAt: 0,
    update: (patch) => set((s) => ({ settings: { ...s.settings, ...patch }, settingsUpdatedAt: Date.now() })),
    updateDeep: (key, patch) => set((s) => ({
      settings: { ...s.settings, [key]: { ...(s.settings[key] as object), ...(patch as object) } },
      settingsUpdatedAt: Date.now(),
    })),
    reset: () => set({ settings: DEFAULT_SETTINGS, settingsUpdatedAt: Date.now() }),
  }),
  {
    name: 'ftu-settings',
    storage: createJSONStorage(() => idbStorage),
    version: 1,
    /* New settings added in later releases get their defaults instead of undefined. */
    merge: (persisted, current) => {
      const p = (persisted ?? {}) as Partial<SettingsState>;
      const s = (p.settings ?? {}) as Partial<Settings>;
      return {
        ...current, ...p,
        settings: {
          ...DEFAULT_SETTINGS, ...s,
          repeat: { ...DEFAULT_SETTINGS.repeat, ...s.repeat },
          reciteRepeat: { ...DEFAULT_SETTINGS.reciteRepeat, ...s.reciteRepeat },
          prayer: { ...DEFAULT_SETTINGS.prayer, ...s.prayer },
          notify: { ...DEFAULT_SETTINGS.notify, ...s.notify },
          trackSunnah: { ...DEFAULT_SETTINGS.trackSunnah, ...s.trackSunnah },
        },
      };
    },
  },
));

/* ── Progress ─────────────────────────────────────────────────────── */
type Coll<T> = Record<string, T>;

interface ProgressState {
  sessions: Coll<Session>;
  goals: Coll<Goal>;
  prayers: Coll<PrayerDay>;
  adhkar: Coll<AdhkarDay>;
  bookmarks: Coll<Bookmark>;
  lectureMarks: Coll<LectureMark>;
  fasts: Coll<FastDay>;
  periods: Coll<PeriodEntry>;
  khatmahs: Coll<Khatmah>;
  ayahLog: Coll<AyahDay>;
  dhikr: Coll<DhikrDay>;
  /** the memorisation plan (one, id "main"), each memorised page's revision state, and a daily log */
  hifz: Coll<HifzPlan>;
  hifzPages: Coll<HifzPage>;
  hifzLog: Coll<HifzDay>;
  /** lessons as saved, so one saved too soon can be marked "not memorised" */
  hifzLessons: Coll<HifzLessonRec>;
  lastRead: LastRead | null;
  lastReadUpdatedAt: number;
  addSession: (s: Omit<Session, 'id' | 'updatedAt' | 'dayKey'> & { dayKey?: string }) => void;
  upsertGoal: (g: Omit<Goal, 'updatedAt' | 'id' | 'createdAt'> & { id?: string; createdAt?: number }) => void;
  deleteGoal: (id: string) => void;
  /** at: when it was prayed ("I've prayed" now) — omitted when a status is just chosen from the list. */
  markPrayer: (dayKey: string, prayer: keyof PrayerDay['marks'], status: PrayerStatus | null, at?: number) => void;
  markSunnah: (dayKey: string, key: SunnahKey, rakats: number | null) => void;
  markFast: (dayKey: string, entry: { status: FastStatus; kind: FastKind; reason?: FastReason } | null) => void;
  startPeriod: (dayKey: string, kind?: PeriodEntry['kind']) => void;
  endPeriod: (id: string, dayKey: string) => void;
  savePeriod: (p: Omit<PeriodEntry, 'updatedAt' | 'id'> & { id?: string }) => void;
  deletePeriod: (id: string) => void;
  saveKhatmah: (k: Omit<Khatmah, 'updatedAt' | 'id' | 'createdAt' | 'position' | 'completions' | 'cycleStart'> & Partial<Pick<Khatmah, 'id' | 'position' | 'completions' | 'cycleStart'>>) => string;
  deleteKhatmah: (id: string) => void;
  /** bring plans up to date for a new day / new cycle */
  rolloverKhatmahs: (today: string) => void;
  /** one āyah read in a plan; returns true when that completes the khatmah */
  /** pageDone: this āyah is the last on its mushaf page. */
  readAyah: (id: string, today: string, letters: number, pageDone?: boolean) => boolean;
  /** go back one āyah and take it back off today's tally (re-reading it counts once) */
  stepBack: (id: string, today: string, letters: number, pageDone?: boolean) => void;
  setDhikrCount: (dayKey: string, when: 'morning' | 'evening', id: number, count: number, complete: boolean) => void;
  resetAdhkar: (dayKey: string, when: 'morning' | 'evening') => void;
  /** dhikr counter: add (or, with a negative delta, undo) today's count */
  addDhikr: (dayKey: string, id: string, delta: number) => void;
  /** pass over a dhikr (or undo); complete: everything is now done or skipped */
  skipDhikr: (dayKey: string, when: 'morning' | 'evening', id: number, skip: boolean, complete: boolean) => void;
  /** "I've finished my adhkar" — counts the set as done even with some left */
  finishAdhkar: (dayKey: string, when: 'morning' | 'evening') => void;
  /** create or change the hifz plan; surahs newly marked as known go straight into revision */
  saveHifz: (p: Omit<HifzPlan, 'id' | 'updatedAt' | 'createdAt' | 'position'> & { position?: number }, today: string) => void;
  /** today's lesson memorised: move on, and start its pages' week of daily revision */
  learnHifzLesson: (lesson: Lesson, ids: number[], today: string) => void;
  gradeHifzPage: (page: number, g: Grade, today: string) => void;
  /** a saved lesson isn't memorised after all: back into the lessons, its pages out of revision if nothing memorised is left on them */
  unlearnHifzLesson: (id: string) => void;
  /** remove the plan and its revision history */
  resetHifz: () => void;
  toggleBookmark: (verseKey: string) => void;
  /** save a lecture video, or take it off the saved list */
  toggleLectureMark: (l: Pick<LectureMark, 'id' | 'title' | 'channel' | 'channelId' | 'part'>) => void;
  /** remember which part of a saved lecture you're on */
  setLectureMarkPart: (id: string, part: number) => void;
  setLastRead: (lr: Omit<LastRead, 'at'>) => void;
  /** merge a server snapshot — last writer wins per key */
  mergeRemote: (remote: Partial<Pick<ProgressState, 'sessions' | 'goals' | 'prayers' | 'adhkar' | 'bookmarks' | 'lectureMarks' | 'fasts' | 'periods' | 'khatmahs' | 'ayahLog' | 'dhikr' | 'hifz' | 'hifzPages' | 'hifzLog' | 'hifzLessons'>> & { lastRead?: LastRead | null }) => void;
}

const today = () => dayKeyIn(useSettings.getState().settings.prayer.tz);

export function mergeColl<T extends { updatedAt: number }>(a: Coll<T> = {}, b: Coll<T> = {}): Coll<T> {
  const out: Coll<T> = { ...a };
  for (const [k, v] of Object.entries(b)) if (!out[k] || (v?.updatedAt ?? 0) > (out[k].updatedAt ?? 0)) out[k] = v;
  return out;
}

export const useProgress = create<ProgressState>()(persist(
  (set) => ({
    sessions: {}, goals: {}, prayers: {}, adhkar: {}, bookmarks: {}, lectureMarks: {}, fasts: {}, periods: {}, khatmahs: {}, ayahLog: {}, dhikr: {}, hifz: {}, hifzPages: {}, hifzLog: {}, hifzLessons: {}, lastRead: null, lastReadUpdatedAt: 0,

    addSession: (s) => set((st) => {
      if (s.seconds < 5) return st; // accidental taps aren't sessions
      const id = uid();
      return { sessions: { ...st.sessions, [id]: { ...s, id, dayKey: s.dayKey ?? today(), updatedAt: Date.now() } } };
    }),

    upsertGoal: (g) => set((st) => {
      const id = g.id ?? uid();
      const prev = st.goals[id];
      return { goals: { ...st.goals, [id]: { ...prev, ...g, id, createdAt: g.createdAt ?? prev?.createdAt ?? Date.now(), updatedAt: Date.now() } as Goal } };
    }),
    deleteGoal: (id) => set((st) => st.goals[id] ? { goals: { ...st.goals, [id]: { ...st.goals[id], deleted: true, active: false, updatedAt: Date.now() } } } : st),

    markPrayer: (dayKey, prayer, status, at) => set((st) => {
      const prev = st.prayers[dayKey] ?? { dayKey, marks: {}, updatedAt: 0 };
      const marks = { ...prev.marks };
      const prayedAt = { ...prev.prayedAt };
      if (status) marks[prayer] = status; else delete marks[prayer];
      if (status && at) prayedAt[prayer] = at; else delete prayedAt[prayer];
      return { prayers: { ...st.prayers, [dayKey]: { ...prev, dayKey, marks, prayedAt, updatedAt: Date.now() } } };
    }),

    markSunnah: (dayKey, key, rakats) => set((st) => {
      const prev = st.prayers[dayKey] ?? { dayKey, marks: {}, updatedAt: 0 };
      const sunnah = { ...prev.sunnah };
      if (rakats && rakats > 0) sunnah[key] = rakats; else delete sunnah[key];
      return { prayers: { ...st.prayers, [dayKey]: { ...prev, sunnah, updatedAt: Date.now() } } };
    }),

    setDhikrCount: (dayKey, when, id, count, complete) => set((st) => {
      const prev = st.adhkar[dayKey] ?? { dayKey, updatedAt: 0 };
      const part = prev[when] ?? { counts: {} };
      const counts = { ...part.counts, [id]: count };
      return { adhkar: { ...st.adhkar, [dayKey]: { ...prev, [when]: { ...part, counts, doneAt: complete ? (part.doneAt ?? Date.now()) : undefined }, updatedAt: Date.now() } } };
    }),
    skipDhikr: (dayKey, when, id, skip, complete) => set((st) => {
      const prev = st.adhkar[dayKey] ?? { dayKey, updatedAt: 0 };
      const part = prev[when] ?? { counts: {} };
      const skipped = skip ? [...new Set([...(part.skipped ?? []), id])] : (part.skipped ?? []).filter((x) => x !== id);
      return { adhkar: { ...st.adhkar, [dayKey]: { ...prev, [when]: { ...part, skipped, doneAt: complete ? (part.doneAt ?? Date.now()) : part.doneAt }, updatedAt: Date.now() } } };
    }),
    addDhikr: (dayKey, id, delta) => set((st) => {
      const day = st.dhikr?.[dayKey] ?? { dayKey, counts: {}, updatedAt: 0 };
      const n = Math.max(0, (day.counts[id] ?? 0) + delta);
      return { dhikr: { ...st.dhikr, [dayKey]: { ...day, counts: { ...day.counts, [id]: n }, ...(delta > 0 ? { last: id } : {}), updatedAt: Date.now() } } };
    }),
    finishAdhkar: (dayKey, when) => set((st) => {
      const prev = st.adhkar[dayKey] ?? { dayKey, updatedAt: 0 };
      const part = prev[when] ?? { counts: {} };
      return { adhkar: { ...st.adhkar, [dayKey]: { ...prev, [when]: { ...part, doneAt: part.doneAt ?? Date.now() }, updatedAt: Date.now() } } };
    }),
    resetAdhkar: (dayKey, when) => set((st) => {
      const prev = st.adhkar[dayKey];
      if (!prev) return st;
      return { adhkar: { ...st.adhkar, [dayKey]: { ...prev, [when]: { counts: {}, skipped: [] }, updatedAt: Date.now() } } };
    }),

    markFast: (dayKey, entry) => set((st) => ({
      fasts: { ...st.fasts, [dayKey]: entry ? { dayKey, ...entry, updatedAt: Date.now() } : { ...(st.fasts[dayKey] ?? { dayKey, status: 'notfasting', kind: 'voluntary' }), deleted: true, updatedAt: Date.now() } },
    })),

    startPeriod: (dayKey, kind = 'hayd') => set((st) => {
      const id = uid();
      return { periods: { ...st.periods, [id]: { id, kind, start: dayKey, updatedAt: Date.now() } } };
    }),
    endPeriod: (id, dayKey) => set((st) => st.periods[id] ? { periods: { ...st.periods, [id]: { ...st.periods[id], end: dayKey < st.periods[id].start ? st.periods[id].start : dayKey, updatedAt: Date.now() } } } : st),
    savePeriod: (p) => set((st) => {
      const id = p.id ?? uid();
      return { periods: { ...st.periods, [id]: { ...p, id, updatedAt: Date.now() } } };
    }),
    deletePeriod: (id) => set((st) => st.periods[id] ? { periods: { ...st.periods, [id]: { ...st.periods[id], deleted: true, updatedAt: Date.now() } } } : st),

    saveKhatmah: (k) => {
      const id = k.id ?? uid();
      set((st) => {
        const prev = st.khatmahs[id];
        const today = dayKeyIn(useSettings.getState().settings.prayer.tz);
        return { khatmahs: { ...st.khatmahs, [id]: { ...(prev ?? { position: 0, completions: 0, cycleStart: today }), ...k, id, createdAt: prev?.createdAt ?? Date.now(), updatedAt: Date.now(), deleted: false } as Khatmah } };
      });
      return id;
    },
    deleteKhatmah: (id) => set((st) => st.khatmahs[id] ? { khatmahs: { ...st.khatmahs, [id]: { ...st.khatmahs[id], deleted: true, updatedAt: Date.now() } } } : st),
    rolloverKhatmahs: (today) => set((st) => {
      let changed = false;
      const next = { ...st.khatmahs };
      for (const [id, k] of Object.entries(st.khatmahs)) {
        if (k.deleted) continue;
        const r = rollover(k, today);
        if (r !== k) { next[id] = { ...r, updatedAt: Date.now() }; changed = true; }
      }
      return changed ? { khatmahs: next } : st;
    }),
    readAyah: (id, today, letters, pageDone = false) => {
      let completed = false;
      set((st) => {
        const k0 = st.khatmahs[id];
        if (!k0) return st;
        const k = rollover(k0, today);
        const total = scopeKeys(k.scope).length;
        const position = k.position + 1;
        completed = position >= total;
        const plan: Khatmah = {
          ...k,
          position: completed ? 0 : position,
          completions: completed ? k.completions + 1 : k.completions,
          cycleStart: completed ? today : k.cycleStart,
          todayKey: today,
          // A finished round starts the next from 0: what you read today belonged to the round you finished.
          todayCount: completed ? 0 : (k.todayKey === today ? k.todayCount ?? 0 : 0) + 1,
          updatedAt: Date.now(),
        };
        const day = st.ayahLog[today] ?? { dayKey: today, ayahs: 0, letters: 0, hasanat: 0, updatedAt: 0 };
        return {
          khatmahs: { ...st.khatmahs, [id]: plan },
          ayahLog: { ...st.ayahLog, [today]: { ...day, ayahs: day.ayahs + 1, letters: day.letters + letters, hasanat: day.hasanat + letters * 10, pages: (day.pages ?? 0) + (pageDone ? 1 : 0), updatedAt: Date.now() } },
        };
      });
      return completed;
    },
    stepBack: (id, today, letters, pageDone = false) => set((st) => {
      const k = st.khatmahs[id];
      if (!k || k.position === 0) return st;
      const counted = k.todayKey === today && (k.todayCount ?? 0) > 0;
      const day = st.ayahLog[today];
      return {
        khatmahs: { ...st.khatmahs, [id]: { ...k, position: k.position - 1, todayCount: counted ? (k.todayCount ?? 1) - 1 : k.todayCount, updatedAt: Date.now() } },
        ...(counted && day ? { ayahLog: { ...st.ayahLog, [today]: { ...day, ayahs: Math.max(0, day.ayahs - 1), letters: Math.max(0, day.letters - letters), hasanat: Math.max(0, day.hasanat - letters * 10), pages: Math.max(0, (day.pages ?? 0) - (pageDone ? 1 : 0)), updatedAt: Date.now() } } } : {}),
      };
    }),

    toggleBookmark: (verseKey) => set((st) => {
      const prev = st.bookmarks[verseKey];
      const on = !prev || prev.deleted;
      return { bookmarks: { ...st.bookmarks, [verseKey]: { id: verseKey, verseKey, createdAt: prev?.createdAt ?? Date.now(), updatedAt: Date.now(), deleted: !on } } };
    }),

    toggleLectureMark: (l) => set((st) => {
      const prev = st.lectureMarks?.[l.id];
      const on = !prev || prev.deleted;
      const part = l.part ?? prev?.part;
      return { lectureMarks: { ...st.lectureMarks, [l.id]: { id: l.id, title: l.title, channel: l.channel, channelId: l.channelId, createdAt: on ? Date.now() : prev?.createdAt ?? Date.now(), updatedAt: Date.now(), deleted: !on, ...(part != null ? { part } : {}) } } };
    }),

    setLectureMarkPart: (id, part) => set((st) => {
      const m = st.lectureMarks?.[id];
      if (!m || m.deleted || m.part === part) return st;
      return { lectureMarks: { ...st.lectureMarks, [id]: { ...m, part, updatedAt: Date.now() } } };
    }),

    saveHifz: (p, today) => set((st) => {
      const prev = st.hifz.main && !st.hifz.main.deleted ? st.hifz.main : null;
      const plan: HifzPlan = { position: 0, ...prev, ...p, id: 'main', createdAt: prev?.createdAt ?? Date.now(), updatedAt: Date.now(), deleted: false };
      // New known surahs (or order) shift the learning order: keep what's learnt.
      if (prev && (prev.order !== plan.order || prev.known.join() !== plan.known.join() || !!prev.sunnahFirst !== !!plan.sunnahFirst)) plan.position = repositionFor(prev, plan.order, plan.known, !!plan.sunnahFirst);
      // A new goal is measured from today.
      if (!prev || prev.targetDay !== plan.targetDay || prev.position !== plan.position || prev.order !== plan.order || !!prev.sunnahFirst !== !!plan.sunnahFirst) { plan.startDay = today; plan.startPosition = plan.position; }
      // A surah newly marked as known: its pages go into revision (pages you already revise keep their schedule).
      const added = plan.known.filter((s) => !prev?.known.includes(s));
      const seeded = seedKnown(st.hifzPages, added, today);
      return { hifz: { ...st.hifz, main: plan }, hifzPages: { ...st.hifzPages, ...Object.fromEntries(seeded.map((x) => [x.id, x])) } };
    }),

    learnHifzLesson: (lesson, ids, today) => set((st) => {
      const plan = st.hifz.main;
      if (!plan || plan.deleted) return st;
      const again = new Set(plan.relearn ?? []);
      // A lesson being learnt again (marked "not memorised" earlier) — or the next new one. Anything else: a double tap.
      const isRelearn = lesson.relearn && ids.length > 0 && ids.every((id) => again.has(id));
      if (!isRelearn && plan.position !== lesson.from) return st;
      const next: HifzPlan = isRelearn
        ? { ...plan, relearn: (plan.relearn ?? []).filter((id) => !ids.includes(id)), updatedAt: Date.now() }
        : { ...plan, position: lesson.to, updatedAt: Date.now() };
      const pagesNow = afterLesson(st.hifzPages, ids, today);
      const day = st.hifzLog[today] ?? { dayKey: today, newAyat: 0, reviewed: 0, updatedAt: 0 };
      const rec: HifzLessonRec = { id: `${ids[0]}-${today}-${Date.now().toString(36)}`, day: today, ids: [...ids], updatedAt: Date.now() };
      return {
        hifz: { ...st.hifz, main: next },
        hifzPages: { ...st.hifzPages, ...Object.fromEntries(pagesNow.map((x) => [x.id, x])) },
        hifzLog: { ...st.hifzLog, [today]: { ...day, newAyat: day.newAyat + ids.length, updatedAt: Date.now() } },
        hifzLessons: { ...st.hifzLessons, [rec.id]: rec },
      };
    }),

    unlearnHifzLesson: (id) => set((st) => {
      const plan = st.hifz.main, rec = st.hifzLessons?.[id];
      if (!plan || plan.deleted || !rec || rec.deleted) return st;
      const next = { ...unlearn(plan, rec.ids), updatedAt: Date.now() };
      // Pages with nothing memorised left on them leave revision.
      const still = new Set([...memorisedIds(next)].map(pageOfId));
      const pages = { ...st.hifzPages };
      for (const p of new Set(rec.ids.map(pageOfId))) {
        const pg = pages[String(p)];
        if (pg && !pg.deleted && !still.has(p)) pages[String(p)] = { ...pg, deleted: true, updatedAt: Date.now() };
      }
      const day = st.hifzLog[rec.day];
      return {
        hifz: { ...st.hifz, main: next },
        hifzPages: pages,
        hifzLessons: { ...st.hifzLessons, [id]: { ...rec, deleted: true, updatedAt: Date.now() } },
        ...(day ? { hifzLog: { ...st.hifzLog, [rec.day]: { ...day, newAyat: Math.max(0, day.newAyat - rec.ids.length), updatedAt: Date.now() } } } : {}),
      };
    }),

    gradeHifzPage: (page, g, today) => set((st) => {
      const p = st.hifzPages[String(page)];
      if (!p || p.deleted) return st;
      const first = p.last?.day !== today;   // re-grading the same page today doesn't count twice
      const day = st.hifzLog[today] ?? { dayKey: today, newAyat: 0, reviewed: 0, updatedAt: 0 };
      return {
        hifzPages: { ...st.hifzPages, [p.id]: gradePage(p, g, today) },
        hifzLog: { ...st.hifzLog, [today]: { ...day, reviewed: day.reviewed + (first ? 1 : 0), updatedAt: Date.now() } },
      };
    }),

    resetHifz: () => set((st) => ({
      hifz: st.hifz.main ? { main: { ...st.hifz.main, deleted: true, updatedAt: Date.now() } } : st.hifz,
      hifzPages: Object.fromEntries(Object.entries(st.hifzPages).map(([k, v]) => [k, { ...v, deleted: true, updatedAt: Date.now() }])),
    })),

    setLastRead: (lr) => set({ lastRead: { ...lr, at: Date.now() }, lastReadUpdatedAt: Date.now() }),

    mergeRemote: (r) => set((st) => ({
      sessions: mergeColl(st.sessions, r.sessions),
      goals: mergeColl(st.goals, r.goals),
      prayers: mergeColl(st.prayers, r.prayers),
      adhkar: mergeColl(st.adhkar, r.adhkar),
      bookmarks: mergeColl(st.bookmarks, r.bookmarks),
      lectureMarks: mergeColl(st.lectureMarks, r.lectureMarks),
      fasts: mergeColl(st.fasts, r.fasts),
      periods: mergeColl(st.periods, r.periods),
      khatmahs: mergeColl(st.khatmahs, r.khatmahs),
      ayahLog: mergeColl(st.ayahLog, r.ayahLog),
      dhikr: mergeColl(st.dhikr, r.dhikr),
      hifz: mergeColl(st.hifz, r.hifz),
      hifzPages: mergeColl(st.hifzPages, r.hifzPages),
      hifzLog: mergeColl(st.hifzLog, r.hifzLog),
      hifzLessons: mergeColl(st.hifzLessons, r.hifzLessons),
      lastRead: r.lastRead && (!st.lastRead || r.lastRead.at > st.lastRead.at) ? r.lastRead : st.lastRead,
    })),
  }),
  { name: 'ftu-progress', storage: createJSONStorage(() => idbStorage), version: 1 },
));

export const alive = <T extends { deleted?: boolean }>(c: Coll<T>) => Object.values(c).filter((x) => !x.deleted);

if (import.meta.env.DEV && typeof window !== 'undefined') Object.assign(window, { __settings: useSettings, __progress: useProgress });
